import React, { useState, useEffect } from 'react';
import { Box, Container, Paper, Tabs, Tab, Typography, CircularProgress, Alert } from '@mui/material';
import { useKeycloak } from '@react-keycloak/web';
import ClientManagement from '../admin/ClientManagement';
import DepartmentManagement from '../admin/DepartmentManagement';
import TeamManagement from '../admin/TeamManagement';
import UserManagement from '../admin/UserManagement';
import HierarchyView from '../admin/HierarchyView';
import { getCurrentUser } from '../api/services/userService/User';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `admin-tab-${index}`,
    'aria-controls': `admin-tabpanel-${index}`,
  };
}

export default function AdminPage() {
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const { keycloak } = useKeycloak();

  const fetchCurrentUser = async () => {
    try {
      await keycloak.updateToken(30);
      const response = await getCurrentUser(keycloak.token);
      
      if (response.success) {
        setCurrentUser(response.user);
        
        const permissions = {
          isSuperAdmin: response.user.superAdmin,
          clientIds: [],
          departmentIds: [],
          teamIds: [],
          clientRoles: {},
          departmentRoles: {},
          teamRoles: {}
        };

        if (response.user.client && response.user.client.client) {
          permissions.clientIds.push(response.user.client.client.id);
          permissions.clientRoles[response.user.client.client.id] = response.user.client.client.role;
        }

        if (response.user.departments && Array.isArray(response.user.departments)) {
          response.user.departments.forEach(dept => {
            if (dept.department) {
              permissions.departmentIds.push(dept.department.id);
              permissions.departmentRoles[dept.department.id] = dept.department.role;
            }
          });
        }

        if (response.user.teams && Array.isArray(response.user.teams)) {
          response.user.teams.forEach(team => {
            if (team.team) {
              permissions.teamIds.push(team.team.id);
              permissions.teamRoles[team.team.id] = team.team.role;
            }
          });
        }

        setUserPermissions(permissions);
      } else {
        setError(response.error || 'Failed to fetch user data');
      }
    } catch (err) {
      console.error('Error fetching current user:', err);
      setError(err.message || 'Failed to fetch user data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (keycloak.token) {
      fetchCurrentUser();
    }
  }, [keycloak.token]);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const getAvailableTabs = () => {
    if (!userPermissions) return [];
    
    const tabs = [];
    
    if (userPermissions.isSuperAdmin || 
        userPermissions.clientIds.length > 0 || 
        userPermissions.departmentIds.length > 0 || 
        userPermissions.teamIds.length > 0) {
      tabs.push({ label: "Hierarchy", index: 0 });
    }
    
    if (userPermissions.isSuperAdmin || userPermissions.clientIds.length > 0) {
      tabs.push({ label: "Clients", index: tabs.length });
    }
    
    if (userPermissions.isSuperAdmin || 
        userPermissions.clientIds.length > 0 || 
        userPermissions.departmentIds.length > 0) {
      tabs.push({ label: "Departments", index: tabs.length });
    }
    
    if (userPermissions.isSuperAdmin || 
        userPermissions.departmentIds.length > 0 || 
        userPermissions.teamIds.length > 0) {
      tabs.push({ label: "Teams", index: tabs.length });
    }
    
    tabs.push({ label: "Users", index: tabs.length });
    
    return tabs;
  };

  const getTabComponent = (tabLabel, index) => {
    switch (tabLabel) {
      case "Hierarchy":
        return <HierarchyView key="hierarchy-view" userPermissions={userPermissions} />;
      case "Clients":
        return <ClientManagement key="client-management" userPermissions={userPermissions} />;
      case "Departments":
        return <DepartmentManagement key="department-management" userPermissions={userPermissions} />;
      case "Teams":
        return <TeamManagement key="team-management" userPermissions={userPermissions} />;
      case "Users":
        return <UserManagement key="user-management" userPermissions={userPermissions} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const availableTabs = getAvailableTabs();

  return (
    <Container maxWidth="100%" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Admin Dashboard
        </Typography>
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabIndex} 
            onChange={handleTabChange} 
            aria-label="admin dashboard tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            {availableTabs.map((tab, idx) => (
              <Tab key={tab.label} label={tab.label} {...a11yProps(idx)} />
            ))}
          </Tabs>
        </Box>

        {availableTabs.map((tab, idx) => (
          <TabPanel key={tab.label} value={tabIndex} index={idx}>
            {getTabComponent(tab.label, idx)}
          </TabPanel>
        ))}
      </Paper>
    </Container>
  );
} 